import {ofType} from 'redux-observable';
import {of} from 'rxjs';
import {
    catchError,
    debounceTime,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import type {GatewayError} from '@yandex-data-ui/gateway';

import {APICollectionResult} from '~shared/types';

import {WikiEpicDeps} from '~ui/store/deps';
import {WikiAction, WikiEpic, WikiState} from '~ui/store/types';
import {batchActions} from '~ui/store/actions';
import toaster from '~ui/utils/toaster';

import {BasePanelState, PanelError} from './types';

type StateGetter<Item, Filters extends object> = (
    state: WikiState,
) => BasePanelState<Item, Filters>;

type Actions<ServerItem> = {
    setLoading(payload: boolean): WikiAction;
    setLoadingMore(payload: boolean): WikiAction;
    setError(payload: GatewayError): WikiAction;
    setItems(payload: APICollectionResult<ServerItem>): WikiAction;
    addItems(payload: APICollectionResult<ServerItem>): WikiAction;
    reset(): WikiAction;
};

export const makeEpic =
    <Item, Filters extends object, ServerItem>(
        stateGetter: StateGetter<Item, Filters>,
        request: (
            state: BasePanelState<Item, Filters>,
            deps: WikiEpicDeps,
        ) => Promise<APICollectionResult<ServerItem>>,
        actions: Actions<ServerItem>,
        listenActionTypes: string[],
        errorMessage: string,
    ): WikiEpic =>
    (action$, state$, deps) => {
        return action$.pipe(
            ofType(...listenActionTypes),
            debounceTime(300),
            withLatestFrom(state$),
            map(([, state]) => stateGetter(state)),
            switchMap((state) => {
                return of(state).pipe(
                    mergeMap((state) => {
                        return request(state, deps);
                    }),
                    map((result) => {
                        if (state.isFirstPage) {
                            return batchActions([
                                actions.setItems(result),
                                actions.setLoading(false),
                            ]);
                        }

                        return batchActions([
                            actions.addItems(result),
                            actions.setLoadingMore(false),
                        ]);
                    }),
                    catchError((error: GatewayError) => {
                        console.error(error);

                        if (error.code !== PanelError.UserFeatureIsProcessing) {
                            toaster.createErrorToast('get-panel-data', errorMessage);
                        }

                        return of(
                            batchActions([
                                actions.setLoading(false),
                                actions.setLoadingMore(false),
                                actions.setError(error),
                            ]),
                        );
                    }),
                    takeUntil(action$.ofType(...listenActionTypes, actions.reset)),
                );
            }),
        );
    };
